import type Transaction from "byzantine/src/Transaction";

/**
 * Bucket key for transactions with no category. This is an internal sentinel,
 * not display text.
 */
export const UNCATEGORIZED = "Uncategorized";

export type Direction = "debit" | "credit";

export type CategoryTotal = {
  category: string;
  total: number;
};

export type CategorySummary = {
  slices: CategoryTotal[];
  total: number;
};

/**
 * Aggregates transactions in one direction into per-category totals, sorted
 * descending so slice order and legend order stay in sync.
 */
export function categoryTotals(
  transactions: Transaction[],
  direction: Direction
): CategorySummary {
  const totals = transactions.reduce((acc, txn) => {
    if (txn.type !== direction) return acc;

    const amount = Math.abs(Number(txn.amount));
    if (!Number.isFinite(amount) || amount === 0) return acc;

    const category = txn.category?.trim() || UNCATEGORIZED;
    acc.set(category, (acc.get(category) ?? 0) + amount);
    return acc;
  }, new Map<string, number>());

  const slices = [...totals.entries()]
    .map(([category, total]) => ({ category, total }))
    .sort((a, b) => b.total - a.total);

  const total = slices.reduce((sum, slice) => sum + slice.total, 0);

  return { slices, total };
}
